<template>
     <transition name="modal">
        <div v-show="hasModals" class="modal-container">
            <div class="modal-box" :class="{[modal.id]: true}" tabindex="0" v-for="(modal, $index) in modals" @keydown.esc="!modal.hideHeaderControls ? close($index, 'keydown') : null">

                <header class="modal-header" v-if="!modal.hideHeader">
                    <p class="modal-title font-bold">
                        <span v-html="getTitle(modal.title)"></span>
                    </p>

                    <div v-if="!modal.hideHeaderControls" class="modal-controls flex inline">
                        <span @click.stop="close($index)" class="hover-opacity close-action">
                            <BaseIcon name="close" size="12"/>
                        </span>
                    </div>
                </header>

                <div class="flex">
                    <div class="modal-content" :class="{ 'prompt': Boolean(modal.prompt) }">
                        <p v-if="modal.subtitle" class="form-description">
                            {{ modal.subtitle }}
                        </p>

                        <component v-if="modal.loadedComponent" :is="modal.loadedComponent" :data="modal.props" :schema="modal.form" :ref="modal.component" />
                        <div v-if="modal.prompt">
                            <div class="modal-footer">
                                <button @click="handleConfirm(modal.confirm, true)">Confirm</button>
                                <button class="secondary" @click="handleConfirm(modal.cancel, true)">Cancel</button>
                            </div>
                        </div>
                    </div>
                </div>

            </div>
        </div>
    </transition>
</template>
<script setup>
    import { ref, provide, computed, defineAsyncComponent,markRaw, getCurrentInstance, onMounted } from 'vue';
    import { camelize } from "@/utils/index.js"; 
    import Loader from '@/components/_Loader.vue';

    let $root = getCurrentInstance().appContext.config.globalProperties;
    let modals = ref([]);

    onMounted(() => {
        let $modals = {
            open: open,
            close: close,
            confirm: confirm
        }

        $root.$modals = $modals;
        provide('$modals', $modals);
    });

    let hasModals = computed(() => {
        const hasModals = !!modals.value.filter(modal => !modal.minimized)?.length;

        hasModals
            ? document.documentElement.classList.add('overflow-hidden')
            : document.documentElement.classList.remove('overflow-hidden');

        return hasModals;
    });

    function getTitle(title) {
        // notranslate shortcode [nt][/nt];
        const regex = /\[nt\](.*?)\[\/nt\]/g;
        let modalTitle = title;

        if(regex.test(modalTitle)) {
            modalTitle = modalTitle.replace(/ /g, '&nbsp;').replace(regex, `<span class="notranslate">$1</span>`);
        }

        return modalTitle;
    }

    function open(options){
        if(!options) return;

        if(options.hasOwnProperty('component')) {
            options.loadedComponent = markRaw(loadAsyncComponent(options.component));
        }        

        if(!options.id && options.title){
            options.id = camelize(options.title, true);
        }

        let isModelOpened = modals.value.findIndex(modal => modal.id === options.id);

        if(isModelOpened > -1) {
            modals.value.splice(isModelOpened, 1);
            modals.value.push(options);
        } else {
            modals.value.push(options);   
        }

        setTimeout(() => {
            modalFocus(options);
        });
    }

    async function close(index){
        const modal = modals.value[index];

        if(modal?.hasOwnProperty('onClose')) {
            modal.onClose();
        }

        modals.value.pop();
    }

    async function confirm(message, options) {
        return new Promise((resolve, reject) => {
            let modalOptions = {
                title: message,
                prompt: true,
                confirm: options?.onConfirm || resolve,
                cancel: options?.onCancel || reject,
                subtitle: options?.subtitle,
                input: options?.input,
                props: options?.props
            }

            if(options?.hasOwnProperty('alert')){
                modalOptions.alert = options.alert;
            }

            open(modalOptions);
        });
    }

    function modalFocus(options){
        if(!options?.id) return;

        let modalClass = document.querySelector(`.${options?.id}`);
        if(modalClass) {
            modalClass.focus();
        }
    }

    async function handleConfirm(modal, data){
        return modal(data), close();
    }

    const loadAsyncComponent = (component) => defineAsyncComponent({
        loader: () => typeof component === 'string'
            ? import(`./modals/${component}.vue`)
            : component,
        loadingComponent: Loader
    });
</script>
<style lang="scss" scoped>
    .modal-container{
        position: fixed;
        width: 100%;
        height: 100%;
        background: var(--modal-backdrop);
        z-index: 10;
        top: 0;
        display: grid;
        justify-content: center;
        align-items: center;
        overflow: auto;
        // animation: fade-in 0.2s;
        // &.minimize-container{
        //     display: none;
        // }
    }
    .modal-box{
        // position: absolute;
        // @extend .primary-box;
        box-shadow: var(--shadow);
        margin: auto;
        grid-column: 1;
        grid-row: 1;
        z-index: inherit;
        background-color: var(--foreground);
        animation: fade-in 0.2s 0.1s backwards;
        border: 0;
        background: var(--foreground);
        border: 1px solid var(--border-color);
        border-radius: 6px;
        outline: none;
        // resize: both;
        // max-height: 100%;
        &.main-modal{
            z-index: 1000;
        }
        &.minimized{
            // display: none;
            animation: minimize 0.2s;
        }
        &.full-screen {
            position: fixed;
            transform: initial !important;
            left: var(--spacing-base) !important;
            top: var(--spacing-base) !important;
            bottom: var(--spacing-base) !important;
            right: var(--spacing-base) !important;
            .modal-content{
                width: 100%;
                height: 95dvh;
            }
            .modal-frame{
                min-width: 100%;
                min-height: 100%;
            }
        }
    }

    .modal-header{
        padding: .5rem .5rem .5rem 2rem;
        display: flex;
        justify-content: space-between;
        align-items: center;
        border-top-left-radius: inherit;
        border-top-right-radius: inherit;
        max-height: 95vh;
        cursor: default;
        min-height: 26px;
        .modal-title{
            font-size: var(--text-lg);
            padding-top: 0.2rem;
            text-overflow: ellipsis;
            overflow: hidden;
            white-space: nowrap;
            max-width: 500px;
            color: var(--text-dark);

        }

        &.prompt .modal-title{ 
            max-width: 23rem;
        }

    }

    .modal-content{
        padding: 1.7rem 2rem;
        padding-top: 1rem;
        min-width: 450px;
        // max-height: 95vh;
        // max-width: 95vw;
        width: 100%;
        min-width: 34rem;

        &:focus-visible, &:focus{
            outline: none;
        }

        &.p-0{
            padding: 0;
        }
        &.newTaskGroup{
            min-width: 650px;
        }

        &.prompt{
            min-width: 370px;
            max-width: 600px;
        }

        :deep(.table-wrapper){
            overflow: auto;
            max-height: clamp(15rem, 60dvh, 45rem);
            padding-bottom: 1px;
            margin-bottom: 0;
            scrollbar-gutter: stable;
        }

        :deep(.bar-wrapper){
            border: 1px solid var(--border-input);
        }

        // :deep(table thead){
        //     background: var(--background);
        // }
    }

    .modal-title{
        font-size: var(--text-2xl);
    }

    .minimized-list {
        position: fixed;
        display: flex;
        justify-content: flex-end;
        bottom: 0;
        z-index: 10;
        right: 2em;
        .minimized-item{
            display: none;
            color: var(--background);
            background-color: var(--primary-color);
            margin: 0 .2em;
            border-top-left-radius: .35em;
            border-top-right-radius: .35em;
            &.minimized{
                text-transform: none;
                font-size: var(--text-base);
                display: flex;
                line-height: normal;
            }
            .title{
                width: 28ch;
                white-space: nowrap;
                overflow: hidden;
                text-overflow: ellipsis;
                padding: .5em 0 .35em 1em;
            }
            .close-icon{
                padding: 0.5em 1em 0.35em 1em;
                &:hover{
                    opacity: 0.5;
                }
            }
            &:hover{
                opacity: 0.9;
            }
        }
    }

    .modal-footer{
        padding-top: 1rem;
        text-align: right;
        >*+*{
            margin-left: 0.5rem;
        }
    }

    @media screen and (max-width: 1000px) {
        .modal-box {
            max-width: 95vw;

            .modal-content {
                min-width: 100%;
                max-width: 100%;
            }
        }

        .minimized-list, .minimize-action{
            display: none;
        }
    }

:deep(.form-description){
    word-break: break-word;
}

@keyframes minimize {
    0%{ 
        transform: translate(0,0) scaleY(1);
        opacity: 1;
    }
    100%{ 
        transform: translate(5rem, 5rem) scaleY(0.5);
        opacity: 0;
    }
}

.modal-controls{
    padding: 0 0.5rem;
}
</style>